import { useCallback, useState } from 'react'
import { uploadFileWithPresignedPost, useS3GetUploadPost } from './s3'

export interface FileUploadProgress {
  progress: number
  status: 'pending' | 'uploading' | 'completed' | 'error'
  error?: string
}

// Upload state machine shared by the document drawers: wraps the presigned-POST
// helper with the progress state the file card renders. upload() resolves to
// the s3Key; on failure it records the error state and rethrows.
export function useDocumentFileUpload() {
  const [progress, setProgress] = useState<FileUploadProgress | null>(null)
  const getUploadPost = useS3GetUploadPost()
  // The generated oRPC mutation is untyped (known baseline); pin the shape here
  const getUploadPostAsync = getUploadPost.mutateAsync as unknown as (input: {
    s3Key: string
    contentType: string
  }) => Promise<{ url: string; fields: Record<string, string> }>

  const upload = useCallback(
    async (file: File, s3Key: string): Promise<string> => {
      setProgress({ progress: 0, status: 'uploading' })
      try {
        await uploadFileWithPresignedPost(file, s3Key, getUploadPostAsync, (percent) =>
          setProgress({ progress: percent, status: 'uploading' })
        )
        setProgress({ progress: 100, status: 'completed' })
        return s3Key
      } catch (error) {
        setProgress({
          progress: 0,
          status: 'error',
          error: error instanceof Error ? error.message : 'Upload failed',
        })
        throw error
      }
    },
    [getUploadPostAsync]
  )

  const reset = useCallback(() => setProgress(null), [])

  return { progress, upload, reset }
}
